/**
 * ReturnToSenderPage — `/mail/returns`.
 *
 * Ported from `screens/src/pages/mail/ReturnToSender.jsx`. Lets the clerk
 * select a registered item, capture a reason, and apply the RETURN
 * transition. Reasons are validated client-side; once the backend ships the
 * RTS reason taxonomy, swap the local list for an enum fetch.
 *
 * Backend:
 *   - POST /v1/mail-items/:id/transitions  with event=RETURN.
 */
import { type JSX, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';

import { transitionMailItem } from '../../lib/api/mail-items';
import { PageHeader } from '../shared/StaffUI';

const REASONS = [
  'Wrong address',
  'Refused by recipient',
  'Recipient unavailable (3 attempts)',
  'Prohibited content',
  'Damaged in transit',
  'Other',
] as const;

export function ReturnToSenderPage(): JSX.Element {
  const [itemId, setItemId] = useState<string>('');
  const [reason, setReason] = useState<string>(REASONS[0]);
  const [notes, setNotes] = useState<string>('');
  const queryClient = useQueryClient();

  const mutate = useMutation({
    mutationFn: () =>
      transitionMailItem(itemId.trim(), {
        event: 'RETURN',
        notes: notes.trim().length > 0 ? `${reason} — ${notes.trim()}` : reason,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['mail', 'items'] });
    },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Return to Sender"
        description="Mark an item for return. Records the reason in the audit trail."
      />

      <div className="bg-white border border-border rounded-xl p-5 space-y-4">
        <div>
          <label htmlFor="rts-item" className="text-sm font-semibold text-gray-900 block mb-1">
            Mail item id
          </label>
          <input
            id="rts-item"
            type="text"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            placeholder="ml-..."
            aria-label="Mail item id"
            className="w-full px-3 py-2 text-sm font-mono border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-nipost-green/20"
          />
        </div>
        <div>
          <label htmlFor="rts-reason" className="text-sm font-semibold text-gray-900 block mb-1">
            Reason
          </label>
          <select
            id="rts-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
          >
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="rts-notes" className="text-sm font-semibold text-gray-900 block mb-1">
            Notes (optional)
          </label>
          <textarea
            id="rts-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-border rounded-lg"
          />
        </div>
        <div className="flex items-center justify-end gap-3">
          {mutate.isError ? (
            <span className="text-xs text-red-700">{(mutate.error as Error).message}</span>
          ) : null}
          {mutate.isSuccess ? (
            <span className="text-xs text-nipost-green">Return recorded.</span>
          ) : null}
          <button
            type="button"
            disabled={itemId.trim().length === 0 || mutate.isPending}
            onClick={() => mutate.mutate()}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" /> {mutate.isPending ? 'Recording…' : 'Record return'}
          </button>
        </div>
      </div>
    </div>
  );
}
